import { Link, Stack, Text } from "@certn/cascadia";
import { Card, GhostIconButton, Icon, mq, Space } from "@certn/cascadia-react";
import {
  CascadiaColor,
  CascadiaCustomIcon,
  CascadiaStandardIcon,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCobrandingEnabled } from "@certn/cascadia-react/context/BrandProvider";

export interface PageBannerProps extends PropsWithChildren {
  icon?: CascadiaStandardIcon | CascadiaCustomIcon;
  color?: CascadiaColor;
  pageBannerColor?: CascadiaColor;
  title: string;
  testID?: string;
  onClose: () => void;
}

export function PageBanner({
  testID = "page-banner",
  icon = "sparkles",
  color,
  pageBannerColor,
  title,
  children,
  onClose,
}: PageBannerProps) {
  const { t } = useTranslation();
  const cobrandingEnabled = useCobrandingEnabled();
  const finalIconColor =
    color || (cobrandingEnabled ? "icon-weak" : "icon-accent-tofino");
  const finalPageBannerColor =
    pageBannerColor ||
    (cobrandingEnabled
      ? "background-selected-weak"
      : "background-accent-tofino-weak");
  const styles = getStyles(finalPageBannerColor);
  const navigate = useNavigate();

  return (
    <Card className={styles.card} testID={testID} hideBorder>
      <Stack gap="space-4" className={styles.content}>
        <Stack gap="space-2" orientation="horizontal">
          <Icon testID={`${testID}-icon`} name={icon} color={finalIconColor} />
          <Text style="global/heading-large">{title}</Text>
          <Space />
          <Link
            linkStyle="monochrome"
            testID="remove-file"
            textColor="text-weak"
            textStyle="global/body-secondary-link"
            onPress={() => navigate("/packages")}
          >
            {t("View packages")}
          </Link>
          <GhostIconButton
            aria-label="Close"
            size="small"
            icon="close"
            onPress={onClose}
          />
        </Stack>
        <Stack gap="space-3" orientation="horizontal" className={styles.mobile}>
          {children}
        </Stack>
      </Stack>
    </Card>
  );
}

const getStyles = (pageBannerColor: CascadiaColor) => ({
  card: css({
    borderRadius: "var(--border-radius-3)",
    backgroundColor: `var(--${pageBannerColor}) !important`,
  }),
  content: css({
    padding: "var(--space-5)",
  }),
  mobile: css(
    mq({
      flexWrap: ["wrap"],
    })
  ),
});
